import { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { CONTACT } from "@/i18n/translations";
import LegalDialog from "@/components/LegalDialog";

export const Footer = () => {
    const { t } = useLang();
    const year = new Date().getFullYear();
    const [dialog, setDialog] = useState({ open: false, type: null });

    const openCookieSettings = () => {
        try { window.localStorage.removeItem("othryx.cookie.consent"); } catch {}
        window.location.reload();
    };

    return (
        <footer
            data-testid="site-footer"
            className="relative bg-navy-dark text-white overflow-hidden grain"
        >
            <div className="absolute inset-0 blueprint-bg opacity-[0.08] pointer-events-none" />

            <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 pt-20 pb-10">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-6 border-b border-white/10 pb-12">
                    <div className="md:col-span-5">
                        <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-steel-400">
                            OTHRYX S.R.L. · {CONTACT.domain}
                        </div>
                        <h3 className="font-display text-3xl lg:text-4xl mt-3 leading-[1.05] tracking-tight">
                            {t.footer.tagline}
                        </h3>
                        <p className="mt-5 text-steel-400 max-w-md leading-relaxed text-sm">
                            {t.footer.framework}
                        </p>

                        <div className="mt-6 space-y-2 text-sm">
                            <a
                                href={`mailto:${CONTACT.emailGeneral}`}
                                data-testid="footer-email-general"
                                className="block link-underline"
                            >
                                {CONTACT.emailGeneral}
                            </a>
                            <a
                                href={`mailto:${CONTACT.emailExec}`}
                                data-testid="footer-email-exec"
                                className="block link-underline text-steel-200"
                            >
                                {CONTACT.emailExec}
                            </a>
                            <div className="text-steel-400 pt-2 font-mono-tech text-xs uppercase tracking-wider">
                                DE · <a href={`tel:${CONTACT.phoneDE_Tel}`} className="link-underline text-white">{CONTACT.phoneDE_Display}</a>
                            </div>
                            <div className="text-steel-400 font-mono-tech text-xs uppercase tracking-wider">
                                RO · <a href={`tel:${CONTACT.phoneRO_Tel}`} className="link-underline text-white">{CONTACT.phoneRO_Display}</a>
                            </div>
                        </div>
                    </div>

                    <div className="md:col-span-2 md:col-start-7">
                        <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-steel-400 mb-4">
                            {t.footer.sections.explore}
                        </div>
                        <ul className="space-y-3 text-sm">
                            <li><Link to="/" className="link-underline">{t.nav.home}</Link></li>
                            <li><Link to="/about" className="link-underline">{t.nav.about}</Link></li>
                            <li><Link to="/services" className="link-underline">{t.nav.services}</Link></li>
                            <li><Link to="/contact" className="link-underline">{t.nav.contact}</Link></li>
                        </ul>
                    </div>

                    <div className="md:col-span-2">
                        <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-steel-400 mb-4">
                            {t.footer.sections.capabilities}
                        </div>
                        <ul className="space-y-3 text-sm text-steel-200">
                            {t.home.services.items.map((it) => (
                                <li key={it.code}>{it.title}</li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-3">
                        <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-steel-400 mb-4">
                            {t.footer.sections.legal}
                        </div>
                        <ul className="space-y-3 text-sm">
                            <li>
                                <Link
                                    to="/legal/impressum"
                                    data-testid="footer-impressum"
                                    className="link-underline"
                                >
                                    {t.footer.legal[0]}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/legal/privacy"
                                    data-testid="footer-privacy"
                                    className="link-underline"
                                >
                                    {t.footer.legal[1]}
                                </Link>
                            </li>
                            <li>
                                <button
                                    onClick={openCookieSettings}
                                    data-testid="footer-cookie-settings"
                                    className="link-underline text-start"
                                >
                                    {t.footer.legal[2]}
                                </button>
                            </li>
                        </ul>
                        <div className="mt-6 text-sm text-steel-400">{t.footer.hq}</div>
                    </div>
                </div>

                <div className="pt-8 flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-steel-400">
                        {t.footer.copyright.replace("{year}", year)}
                    </div>
                    <a
                        href={`https://${CONTACT.domainPlain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-steel-200 link-underline"
                    >
                        {CONTACT.domain}
                    </a>
                </div>

                <div aria-hidden="true" className="mt-10 -mb-2 select-none pointer-events-none">
                    <div className="font-display leading-none text-white/[0.06] text-[22vw] md:text-[16vw] tracking-tighter">
                        OTHRYX
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
